import { useCallback, useContext, useEffect, useReducer, useRef } from 'react'

import { ExtLink } from 'tools/Link'
import Markdown from 'tools/Markdown/Simple'
import { Label2 as Label } from 'tools/Uniform'
import { stopEvent } from 'utils/event'

import { CHG, InputContext, R_INPUT, reducer } from './reducer'

export * from './LoadingInput'

/*

immediate changes:
  - no free-form args

future changes:
  - token -> field
  - meta.id -> baseId
  - meta.action -> action
  - component -> table
  - cmeta.id -> tableId
  - meta.* and cmeta.* — for all others just use onSave override or makeVars

onChange is for validating the change, not updating it externally (that's bad behavior)
onChange may return a warning.

*/

export function Input(props) {
  // possibly not necessary to nest this with another function, but it might
  // save a refresh? ... and this assures ref is in initial state
  const ref = useRef(null)
  return <InputWithRef xref={ref} {...props} />
}
export default Input

export function InputWithRef({
  xref,
  value, // shunt into initialValue
  initialValue,
  className = '',
  onError,
  onSave,
  onClick,
  onChange, // NOT THE PREVIOUS ON CHANGE, this is for validation
  onCancel,
  required,
  style,
  wrapClass = 'flex-grow-1',
  wrap2Class = 'flex-grow-1',
  multiline = false,
  lineheight = 1,
  minRows = 1,
  placeholder = '',
  makeVars,
  saveButton,
  showError = true,
  dirty = true,
  debug,
  preview = false,
  prefix,

  // forward args
  token,
  meta = {},
  cmeta = {},
  component,
  table,
  followId,
  markdown = false,
  refresh,
  ...rest
}) {
  initialValue = initialValue ?? value ?? ''
  component = component ?? table

  const reducerState = useReducer(reducer, {
    height: '',
    status: CHG.OK,
    cancel: false,
    error: undefined,
    value: initialValue,
    orig: initialValue,
    className,
    inputClass: className,
    followId,
    xref,
    onError,
    required,
    onSave,
    onChange,
    onClick,
    onCancel,
    style,
    wrapClass: wrapClass + ' flex-items ga3',
    wrap2Class: wrap2Class + ' input-inline relative',
    multiline,
    preview,
    lineheight,
    minRows,
    placeholder,
    makeVars,
    showError,
    dirty,
    debug,
    token,
    meta,
    cmeta,
    component,
    saveButton,
    markdown,
    refresh,
    prefix
  })

  return (
    <InputContext.Provider value={reducerState}>
      <InsideInput {...rest} refresh={refresh} newValue={initialValue} />
    </InputContext.Provider>
  )
}

function InsideInput({
  children,
  refresh,
  newValue,
  info,
  help,
  label,
  labelClass,
  labelAccent,
  callout,
  ...args
}) {
  const [state, dispatch] = useContext(InputContext)
  if (state.debug) console.log(`<Input3 ${state.debug}> top`, { ...state })

  // / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / /
  // doing this with useEffect instead of a callback within useCallback because
  // isMounted becomes important with the time lag
  useEffect(() => {
    let isMounted = true

    if (state.didSave) {
      setTimeout(() => {
        if (isMounted) dispatch({ type: R_INPUT.CHANGE_OK })
      }, 1000)
    }
    return () => {
      isMounted = false
    }
  }, [dispatch, state.didSave])

  // initialize the height properly (for multiline)
  useEffect(() => {
    if (state.height === '') dispatch({ type: R_INPUT.INIT })
  }, [dispatch, state.height])

  // / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / /

  const onSaveHook = useCallback(
    (ev) => {
      // for some reason we're getting called twice, need to figure this out;
      // for now this avoids that double-tap... probably an order-of-operations
      // in updating value in the state mixing w/onChange and this and whatnot...
      if (!state.dirty) {
        if (state.debug)
          console.log(`<Input3 ${state.debug}> not dirty, not making callback`)

        dispatch({ type: R_INPUT.CHANGE_OK })
        return
      }

      if (!state.onSave) {
        if (state.debug) console.log(`<Input3 ${state.debug}> no onSave`)
        return
      }
      const { onSave, ...rest } = state

      const args = { event: ev, ...rest }
      const onFail = (error) =>
        dispatch({
          type: R_INPUT.DID_SAVE,
          value: { status: CHG.FAILED, error },
          event: ev
        })
      const onSuccess = (status = CHG.SAVED, result) =>
        dispatch({ type: R_INPUT.DID_SAVE, value: { status, result }, event: ev })

      if (state.debug)
        console.log(`<Input3:onSave ${state.debug}> making onSave`, {
          args,
          value: ev.target.value
        })
      onSave(
        args,
        // in theory this is the same as state.value, and using this saves us a
        // dependency variable to useCallback
        ev.target.value,
        onSuccess,
        onFail
      )
    },
    [dispatch, state]
  )

  const hasChange = refresh !== state.refresh
  // const { info, label } = state
  useEffect(() => {
    if (hasChange) {
      if (state.debug)
        console.log(`<Input3 ${state.debug}> refresh`, { newValue })
      dispatch({ type: R_INPUT.REFRESH, value: newValue, refresh })
    }
  }, [dispatch, hasChange, refresh, state.debug, newValue])

  // / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / /
  const onChange = (e) => {
    state.onChange && state.onChange(e)
    dispatch({ type: R_INPUT.DID_CHANGE, value: e.target.value, event: e })
  }
  const onFocus = (e) => dispatch({ type: R_INPUT.DID_FOCUS, event: e })
  const onCancel = () => dispatch({ type: R_INPUT.DID_CANCEL })
  const onKeyDown = (e) => didKeyDown(e, onSaveHook, onCancel, state.multiline)

  const showSaveButton =
    state.saveButton && state.status === CHG.DIRTY && !state.cancel

  // / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / / /
  // I don't want the label and info here. I'd rather it be a separate wrapper
  // class, but to leverage the state info (value for maxlen), it is best
  // placed here.
  return (
    <>
      {label && (
        <InputLabel
          state={state}
          help={help}
          label={label}
          accent={labelAccent}
          className={labelClass}
          maxLength={args.maxLength}
        />
      )}
      {info && <div className="fw2 f2 mb3">{info}</div>}
      <div className={state.wrapClass}>
        <div className={state.wrap2Class}>
          {state.prefix}
          {callout && (
            <div className="tc bg-warn" style={{ fontSize: '6pt' }}>
              {callout}
            </div>
          )}
          <textarea
            className={state.inputClass}
            value={state.value}
            ref={state.xref}
            rows={state.minRows}
            style={state.style}
            onKeyDown={onKeyDown}
            onBlur={state.saveButton ? () => {} : onSaveHook}
            onClick={state.onClick}
            onChange={onChange}
            onFocus={onFocus}
            placeholder={state.placeholder}
            {...args}
          />
          <ShowIcon status={state.status} dispatch={dispatch} />
          {state.saveButtonType === 1 && showSaveButton && (
            <div className="flex absolute right-0 z-9999 mt1 gh2">
              <SaveButton
                onSave={() => dispatch({ type: R_INPUT.DID_CANCEL })}
                label={<i className="fas fa-times" />}
                xref={state.xref}
              />
              <SaveButton
                onSave={onSaveHook}
                label={<i className="fas fa-check" />}
                className=""
                xref={state.xref}
              />
            </div>
          )}
        </div>
        {state.saveButtonType === 2 && (
          <SaveButton onSave={onSaveHook} className="large" xref={state.xref}>
            {state.saveButton}
          </SaveButton>
        )}
        {children}
        {state.preview && (
          <div className="ba b--minimize br0 pa3 wmax-50 doc">
            {/* temporary until a richtext editor */}
            <Markdown>{state.value}</Markdown>
          </div>
        )}
      </div>
      {state.showError && state.error && (
        <div className="error f2 mt2">{state.error}</div>
      )}
    </>
  )
}

export const KEYS = {
  SAVE: Symbol('KEY_SAVE'),
  CANCEL: Symbol('KEY_CANCEL')
}

export function keyCheck(ev, multiline) {
  // ENTER
  if (!multiline && ev.keyCode === 13 && !ev.shiftKey) {
    return KEYS.SAVE

    // ESC
  } else if (ev.keyCode === 27) {
    return KEYS.CANCEL
  }
}

export function didKeyDown(ev, onSaveHook, onCancel, multiline) {
  switch (keyCheck(ev, multiline)) {
    case KEYS.SAVE:
      stopEvent(ev)
      onSaveHook(ev)
      return KEYS.SAVE

    case KEYS.CANCEL:
      if (onCancel) {
        stopEvent(ev)
        onCancel()
      }

      return KEYS.CANCEL

    default:
      return undefined
  }
}

function ShowIcon({ dispatch, status }) {
  const setOk = () => dispatch({ type: R_INPUT.CHANGE_OK })
  switch (status) {
    case CHG.FAILED:
      return (
        <div onClick={setOk} className="mark error center-y pa1 br2">
          <i className="red fas fa-times" />
        </div>
      )
    case CHG.SAVED:
      return (
        <div onClick={setOk} className="mark ok center-y pa1 br2">
          <i className="green fas fa-check" />
        </div>
      )
    default:
      return null
  }
}

function SaveButton({ onSave, label, xref, className = 'neutral', children }) {
  return (
    <button
      onClick={(e) => {
        e.currentTarget = xref.current
        e.target = xref.current
        onSave(e)
      }}
      className={className}
    >
      {children ?? label}
    </button>
  )
}

export function ControlledInput({
  value,
  onChange,
  onClick,
  onKeySave,
  className,
  cleanHideSave = false,
  dirty,
  children,
  Component = 'input',
  ...passArgs
}) {
  onKeySave ??= onClick
  return (
    <div className="flex-items gh2 input-inline flex-grow-1">
      <Component
        className={className + (dirty ? ' dirty' : '')}
        value={value}
        onKeyDown={
          onKeySave
            ? (e) => didKeyDown(e, onKeySave, undefined, Component !== 'input')
            : undefined
        }
        onChange={(e) => onChange(e.target.value)}
        {...passArgs}
      />
      {(dirty || !cleanHideSave) && (
        <button disabled={!dirty} onClick={onClick} className="f1 pa2">
          <i className="fas fa-check" />
        </button>
      )}
      {children}
    </div>
  )
}

function InputLabel({ state, help, label, className, labelAccent, maxLength }) {
  const reqwarn = state.required && !state.value
  let args = {}
  if (className) args.margins = ''

  // goofiness to get around Label class issues
  if (reqwarn) {
    if (className) className = className + ' warn'
    else className = ' warn'
  }
  return (
    <Label
      {...args}
      markdown={state.markdown}
      className={className}
      maxLength={maxLength}
      value={state.value}
      dirty={state.status}
      help={help}
    >
      {reqwarn && '* '}
      {label}
      {state.preview && (
        <ExtLink
          to="https://www.markdownguide.org/basic-syntax/"
          className="fw2 i f1"
        >
          Markdown formatting supported
          <i className="fas fa-external-link-alt ml2 f1" />
        </ExtLink>
      )}
    </Label>
  )
}
export { CHG }
